/**
 * Brewery keg production cost (our cost, not the venue's).
 * Port of Briteside computePintPrice kegCost / kegSell only — no pint path.
 */

export const KEG_PRODUCTION_DEFAULTS = {
  wagesPerLitre: 1.4,
  wastagePct: 7.5,
  excisePerLitreAlcohol: 38.999,
  kegLease: 15.08,
  threePlPerLitre: 0.45,
  grossMarginPct: 25,
};

export const KEG_COST_EDITOR_FIELDS = [
  {
    key: 'wagesPerLitre',
    label: 'Wages /L',
    kind: 'money',
    step: 0.1,
  },
  {
    key: 'excisePerLitreAlcohol',
    label: 'Excise /L alcohol',
    kind: 'money',
    step: 0.001,
  },
  {
    key: 'wastagePct',
    label: 'Wastage',
    kind: 'pct',
    step: 0.5,
  },
  {
    key: 'threePlPerLitre',
    label: '3PL /L',
    kind: 'money',
    step: 0.05,
  },
  {
    key: 'grossMarginPct',
    label: 'GP margin',
    kind: 'pct',
    step: 0.5,
  },
  {
    key: 'kegLease',
    label: 'Keg lease',
    kind: 'money',
    step: 0.01,
  },
];

/**
 * @param {{
 *   costPerFinishedL: number|null|undefined,
 *   abvPct: number|null|undefined,
 *   kegSizeLiters?: number,
 *   settings?: object,
 * }} input
 * @returns {{
 *   kegCost: number,
 *   kegSell: number,
 *   breakdown: {
 *     costPerFinishedL: number,
 *     wagesPerLitre: number,
 *     costPerLInclWages: number,
 *     beerCostInKeg: number,
 *     excise: number,
 *     kegLease: number,
 *     threePl: number,
 *     kegSizeLiters: number,
 *     wastagePct: number,
 *   },
 * }|null}
 */
export function computeKegProductionCost({
  costPerFinishedL,
  abvPct,
  kegSizeLiters = 50,
  settings = {},
}) {
  if (costPerFinishedL == null || !Number.isFinite(costPerFinishedL) || costPerFinishedL < 0) {
    return null;
  }
  if (abvPct == null || !Number.isFinite(abvPct) || abvPct < 0) return null;
  if (!(kegSizeLiters > 0) || !Number.isFinite(kegSizeLiters)) return null;

  const wagesPerLitre = settings.wagesPerLitre ?? KEG_PRODUCTION_DEFAULTS.wagesPerLitre;
  const wastagePct = settings.wastagePct ?? KEG_PRODUCTION_DEFAULTS.wastagePct;
  const excisePerLitreAlcohol = settings.excisePerLitreAlcohol
    ?? KEG_PRODUCTION_DEFAULTS.excisePerLitreAlcohol;
  const kegLease = settings.kegLease ?? KEG_PRODUCTION_DEFAULTS.kegLease;
  const threePlPerLitre = settings.threePlPerLitre ?? KEG_PRODUCTION_DEFAULTS.threePlPerLitre;
  const grossMarginPct = settings.grossMarginPct ?? KEG_PRODUCTION_DEFAULTS.grossMarginPct;

  const wastageFrac = wastagePct / 100;
  const marginFrac = grossMarginPct / 100;
  if (!(wastageFrac >= 0 && wastageFrac < 1) || !(marginFrac >= 0 && marginFrac < 1)) {
    return null;
  }

  const costPerLInclWages = costPerFinishedL + wagesPerLitre;
  const beerCostInKeg = (costPerLInclWages / (1 - wastageFrac)) * kegSizeLiters;
  const excise = (abvPct / 100) * excisePerLitreAlcohol * kegSizeLiters;
  const threePl = threePlPerLitre * kegSizeLiters;
  const kegCost = beerCostInKeg + excise + kegLease + threePl;
  const kegSell = kegCost / (1 - marginFrac);

  return {
    kegCost,
    kegSell,
    breakdown: {
      costPerFinishedL,
      wagesPerLitre,
      costPerLInclWages,
      beerCostInKeg,
      excise,
      kegLease,
      threePl,
      kegSizeLiters,
      wastagePct,
    },
  };
}

/** Our wholesale GP% — sell and cost both ex GST. No GST gross-up. */
export function ourGpPct(sellExGst, ourCost) {
  const sell = Number(sellExGst);
  const cost = Number(ourCost);
  if (!Number.isFinite(sell) || sell <= 0 || !Number.isFinite(cost)) return null;
  const gp = ((sell - cost) / sell) * 100;
  if (!Number.isFinite(gp)) return null;
  return Math.round(gp * 10) / 10;
}
